<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');

const otherAgent = computed(() => {
  const uid = currentUser.value?.id;
  return props.conversation.initiator.id === uid
    ? props.conversation.participant
    : props.conversation.initiator;
});

const isActive = computed(
  () => Number(route.params.conversationId) === props.conversation.id
);

const hasUnread = computed(() => props.conversation.unread_count > 0);

const lastMessagePreview = computed(() => {
  const msg = props.conversation.last_message;
  if (!msg) return '';
  const isOwn = msg.sender_id === currentUser.value?.id;
  const prefix = isOwn ? t('AGENT_CHAT.YOU_PREFIX') : '';
  const text = msg.content?.trim()
    ? msg.content.slice(0, 80)
    : t('AGENT_CHAT.ATTACHMENT_PREVIEW');
  return prefix + text;
});

const navigate = () => {
  const accountId = route.params.accountId;
  router.push(
    frontendURL(`accounts/${accountId}/agent-chat/${props.conversation.id}`)
  );
};
</script>

<template>
  <button
    class="relative flex items-start w-full py-3 px-3 cursor-pointer border-b border-n-slate-3 hover:border-n-surface-1 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 text-left"
    :class="{ 'bg-n-alpha-2 dark:bg-n-alpha-3 border-n-surface-1': isActive }"
    @click="navigate"
  >
    <Avatar
      :src="otherAgent.avatar_url"
      :name="otherAgent.name"
      :size="36"
      :status="otherAgent.availability_status"
    />
    <div class="flex-1 min-w-0 ltr:ml-2.5 rtl:mr-2.5">
      <div class="flex items-baseline gap-1 min-w-0">
        <span
          class="text-sm flex-1 min-w-0 truncate"
          :class="
            hasUnread
              ? 'font-semibold text-n-slate-12'
              : 'font-medium text-n-slate-12'
          "
        >
          {{ otherAgent.name }}
        </span>
      </div>
      <p
        class="text-sm mt-0.5 truncate leading-5 min-w-0 ltr:pr-6 rtl:pl-6"
        :class="hasUnread ? 'font-medium text-n-slate-12' : 'text-n-slate-11'"
      >
        {{ lastMessagePreview || '&nbsp;' }}
      </p>
    </div>
    <div
      v-if="hasUnread"
      class="absolute ltr:right-3 rtl:left-3 top-4 ltr:ml-auto rtl:mr-auto mt-1"
    >
      <span
        class="flex h-5 min-w-5 items-center justify-center rounded-full bg-n-brand px-1.5 text-xs font-medium text-white"
      >
        {{ conversation.unread_count > 99 ? '99+' : conversation.unread_count }}
      </span>
    </div>
  </button>
</template>
